import React, { useEffect, useRef } from "react";
import consts from "shared/consts";
import PolygonElement from "frontend/utils/PolygonElement";
import { Ellipse, Group, Rect, Shape } from "react-konva";
import { defaultShapeProperties, Shape as ShapeElement } from "shared/datamodel/schemas/shape";
import { ITraits, Trait } from "../elements-toolbar/elements-toolbar-types";
import { DataLayerBadge } from "../element-dynamic-data/data-layer-badge";
import { textEnabledTraits } from "./text-block-element";
import { parseStrokeWidth } from "shared/util/utils";
import { calcDashProperties } from "frontend/utils/node-utils";
import { Degrees, toRadians } from "frontend/utils/transform";
import { rotate as pointRotate } from "frontend/utils/point-utils";
import ShapesData from "frontend/data/shapes/shapes-visuals";
import { getDefaultSizeForShape } from "frontend/utils/shape-utils";
import { useElementWithRulesProcessing } from "../element-dynamic-data/hooks/use-element-rules-processor";
import { useScaleRule } from "../element-dynamic-data/hooks/use-scale-rule";
import { useTrackRenderCountForTests } from "utils/tests/use-track-render-count-for-tests";
import { useDataLayerErrors } from "../element-dynamic-data/hooks/use-missing-data-badge";
import { distance } from "frontend/geometry/utils";

// points => split by commas+spaces => map to numbers => map to scaled values
function pointsToNumbers(points: string) {
  return points
    .replace(/[\s,]+/g, " ")
    .split(" ")
    .map(Number);
}

export default function ShapeCanvasElement({
  id,
  element,
  onShapeTypeChanged,
  isFrameHighlighted,
}: {
  id: string;
  element: ShapeElement;
  onShapeTypeChanged: any;
  isFrameHighlighted: boolean;
}) {
  useTrackRenderCountForTests(id);

  const { width, height, radius, fill, type, border, dash, strokeWidth, subtype } = {
    ...defaultShapeProperties,
    ...useElementWithRulesProcessing({ id, element }),
  };

  const { scaleX, scaleY } = useScaleRule(id, element);

  const errorType = useDataLayerErrors(id, element);

  const sw = parseStrokeWidth(strokeWidth);

  const currentType = useRef<any>({ type, subtype });
  function drawCommands(
    type: string,
    subtype: string | undefined,
    element: any,
    scaleX: number | undefined,
    scaleY: number | undefined,
    isPdfContext: boolean,
    getFillStyle: () => string | undefined,
    getStrokeStyle: () => string | undefined
  ) {
    if (type == consts.CANVAS_ELEMENTS.SHAPE && subtype) {
      const data = subtype ? (ShapesData as any)[subtype] : undefined;
      const rotDegrees = element.rotate ?? 0;
      const matrix = new DOMMatrix().rotate(rotDegrees).scale(scaleX, scaleY);

      if (!data || !data.segments) {
        console.warn("Invalid data provided to useDrawCommands");
        return [];
      }

      const commands = [];

      for (const cmd of data.segments) {
        if (cmd.type === "path") {
          let d = cmd.d;
          if (typeof cmd.d === "function") {
            d = cmd.d(scaleX, scaleY);
          }

          let path = null;
          let drawFunc = null;

          if (isPdfContext) {
            drawFunc = (c: any) => (c as any).drawSvgPath(d, matrix);
          } else {
            path = new Path2D();
            path.addPath(new Path2D(d), matrix);
          }

          const fillStyle = cmd.fill
            ? cmd.fill === "fill"
              ? getFillStyle()
              : cmd.fill === "stroke"
              ? getStrokeStyle()
              : cmd.fill
            : null;

          const strokeStyle = cmd.stroke
            ? cmd.stroke === "fill"
              ? getFillStyle()
              : cmd.stroke === "stroke"
              ? getStrokeStyle()
              : cmd.stroke
            : null;

          commands.push({ type: "path", path, drawFunc, fillStyle, strokeStyle });
        } else if (cmd.type === "polygon") {
          const r = cmd.cornerRadius || 0;
          const points = typeof cmd.points === "function" ? cmd.points(scaleX, scaleY) : cmd.points;

          const numbers = pointsToNumbers(points);
          const transformedPoints: number[] = [];
          for (let i = 0; i < numbers.length; i += 2) {
            const transformedPoint = matrix.transformPoint({ x: numbers[i], y: numbers[i + 1] });
            transformedPoints.push(transformedPoint.x, transformedPoint.y);
          }

          // The following code builds the command list for drawing a polygon with N points,
          // and with rounded corners (radius can be 0 for non-rounded)
          // It clamps the radius to avoid the arc "folding back" if the segments are too short
          // (see MDN's page about arcTo for examples of this edge case)
          const startX = (transformedPoints[0] + transformedPoints[2]) / 2;
          const startY = (transformedPoints[1] + transformedPoints[3]) / 2;
          let prevX = transformedPoints[0],
            prevY = transformedPoints[1];
          const drawFunc = (c: any) => {
            c.moveTo(startX, startY);
            for (let i = 2; i < transformedPoints.length + 2; i += 2) {
              const x1 = transformedPoints[i % transformedPoints.length];
              const y1 = transformedPoints[(i + 1) % transformedPoints.length];
              const x2 = transformedPoints[(i + 2) % transformedPoints.length];
              const y2 = transformedPoints[(i + 3) % transformedPoints.length];

              // Clamp radius if edges are too short
              // C = r / tan(angle/2) is the distance from corner to arc start/end
              // C must not exceed min(d1, d2) / 2 for polygons
              const d1 = distance(prevX, prevY, x1, y1);
              const d2 = distance(x1, y1, x2, y2);
              const maxC = Math.min(d1, d2) / 2;

              // Calculate interior angle at p1 (the corner point)
              // dir1: from p1 back to prev (incoming edge direction reversed)
              // dir2: from p1 to p2 (outgoing edge direction)
              const dir1 = { x: (prevX - x1) / d1, y: (prevY - y1) / d1 };
              const dir2 = { x: (x2 - x1) / d2, y: (y2 - y1) / d2 };
              const dot = dir1.x * dir2.x + dir1.y * dir2.y;
              const alpha = Math.acos(Math.max(-1, Math.min(1, dot))); // interior angle
              const tanHalfAlpha = Math.abs(Math.tan(alpha / 2));

              // C = r / tan(alpha/2), so max r = maxC * tan(alpha/2)
              const maxRadius = tanHalfAlpha > 0 ? maxC * tanHalfAlpha : 0;
              const clampedR = Math.min(r, maxRadius);

              c.arcTo(x1, y1, x2, y2, clampedR);
              prevX = x1;
              prevY = y1;
            }
            c.closePath();
          };

          const fillStyle = cmd.fill
            ? cmd.fill === "fill"
              ? getFillStyle()
              : cmd.fill === "stroke"
              ? getStrokeStyle()
              : cmd.fill
            : null;

          const strokeStyle = cmd.stroke
            ? cmd.stroke === "fill"
              ? getFillStyle()
              : cmd.stroke === "stroke"
              ? getStrokeStyle()
              : cmd.stroke
            : null;
          commands.push({ type: "polygon", drawFunc, fillStyle, strokeStyle });
        }
      }

      return commands;
    }
  }
  let drawCmds = drawCommands(
    type,
    subtype,
    element,
    scaleX,
    scaleY,
    false,
    () => fill,
    () => border
  );
  const shapeProps = {
    fill,
    stroke: border,
    strokeEnabled: !(border === "transparent" || dash === -1),
    perfectDrawEnabled: false,
    shadowForStrokeEnabled: false,
    strokeWidth: sw,
    hitStrokeWidth: 0,
    ...calcDashProperties(sw, dash),
  };

  const polygonProps = {
    ...shapeProps,
    radius,
    scaleX,
    scaleY,
  };
  const rectProps = {
    ...shapeProps,
    width: width * scaleX,
    height: height * scaleY,
    scaleX: 1 / scaleX,
    scaleY: 1 / scaleY,
  };

  useEffect(() => {
    if (type !== currentType.current.type || subtype != currentType.current.subtype) {
      //call onShapeTypeChanged only when the type changes (this effect is called when type is loaded as well, causing a bug if this check is not here)
      currentType.current = { type, subtype };
      onShapeTypeChanged();
    }
  }, [type, subtype]);

  function renderShapeByType(extraProps: any = {}) {
    if (type == consts.CANVAS_ELEMENTS.SHAPE && subtype) {
      const data = subtype ? (ShapesData as any)[subtype] : undefined;
      const viewbox = data?.viewbox;
      if (!viewbox) return null;
      const [x, y, width, height] = viewbox;
      const center = { x: x + width / 2, y: y + height / 2 };
      const size = { width, height };
      return (
        <Group offset={center} listening={false}>
          <Shape
            listening={false}
            {...shapeProps}
            // these 3 props ensure that getClientRect returns {0,0,0,0} so the
            // shape doesn't affect the parent's bounding box, and that way I can control the bounding-box
            // better for the transformer
            width={0}
            height={0}
            strokeWidth={0}
            sceneFunc={(context, shape) => {
              try {
                const c = context._context; // I want to use the native canvas context so I can use Path2d
                const withFill = shape.fillEnabled();
                const withStroke = shape.strokeEnabled();
                // I have to set all the parameters myself since I won't use Konva's fill/stroke functions
                c.lineWidth = sw;
                c.lineJoin = shape.lineJoin();
                c.lineCap = shape.lineCap();
                c.lineDashOffset = shape.dashOffset();
                c.setLineDash(shape.dashEnabled() ? shape.dash() : []);

                // I cancel the scaling and rotation.
                // 1. scale is cancelled because I don't want stroke to be scaled with the shape
                // 2. rotation is cancelled because in exporting to pdf, js-pdf doesn't handle arc commands with rotation well.
                const rotDegrees = element.rotate ?? 0;
                const rotRadians = (rotDegrees / 180) * Math.PI;
                c.scale(1 / scaleX, 1 / scaleY);
                c.rotate(-rotRadians);
                // Instead of the cancelled transform, I'll build a matrix with the correct transform, and apply it to the points
                if ((c as any).isPdfContext) {
                  drawCmds = drawCommands(
                    type,
                    subtype,
                    element,
                    scaleX,
                    scaleY,
                    (c as any).isPdfContext, // Boolean indicating whether it's a PDF context
                    () => fill, // Function to get fill style
                    () => border // Function to get stroke style
                  );
                }

                if (drawCmds) {
                  for (const command of drawCmds) {
                    c.beginPath();
                    if (command.drawFunc) {
                      command.drawFunc(c);
                    }

                    // Apply fill
                    if (withFill && command.fillStyle) {
                      c.fillStyle = command.fillStyle;
                      command.type == "path" && command.path ? c.fill(command.path) : c.fill();
                    }

                    // Apply stroke
                    if (withStroke && command.strokeStyle) {
                      c.strokeStyle = command.strokeStyle;
                      command.type == "path" && command.path ? c.stroke(command.path) : c.stroke();
                    }
                  }
                }
              } catch (e) {
                console.error("Error while rendering draw commands:", e);
              }
            }}
          />
          {/* a rectangle to cover the box, invisible on canvas,
                appears on Konva's hit canvas and also give size of the shape\
                for the transformer.
            */}
          <Rect
            x={center.x - size.width / 2}
            y={center.y - size.height / 2}
            scaleX={1 / scaleX} // already in the drawing
            scaleY={1 / scaleY}
            width={size.width * scaleX}
            height={size.height * scaleY}
            strokeEnabled={shapeProps.strokeEnabled}
            strokeWidth={shapeProps.strokeWidth}
            stroke="transparent"
            hitStrokeWidth={shapeProps.strokeWidth}
            fillEnabled={true}
            fill="transparent"
          />
        </Group>
      );
    }

    switch (type) {
      case consts.SHAPES.RECT: {
        return <Rect {...rectProps} {...extraProps} />;
      }
      case consts.SHAPES.RECT_ROUNDED: {
        return <Rect cornerRadius={consts.DEFAULTS.ROUNDED_RECT_RADIUS} {...rectProps} {...extraProps} />;
      }
      case consts.SHAPES.DIAMOND: {
        return <PolygonElement sides={4} {...polygonProps} {...extraProps} />;
      }
      case consts.SHAPES.CIRCLE: {
        return (
          <Ellipse
            {...shapeProps}
            {...extraProps}
            radiusX={radius * scaleX}
            radiusY={radius * scaleY}
            scaleX={1 / scaleX}
            scaleY={1 / scaleY}
          />
        );
      }
      case consts.SHAPES.TRIANGLE: {
        return <PolygonElement sides={3} {...polygonProps} {...extraProps} />;
      }
      case consts.SHAPES.HEXAGON: {
        return <PolygonElement sides={6} {...polygonProps} {...extraProps} />;
      }
      default: {
        return <React.Fragment />;
      }
    }
  }

  function renderHighlight() {
    return renderShapeByType({
      fill: "rgba(0, 0, 0, 0.2)",
    });
  }

  return (
    <>
      {renderShapeByType()}
      {isFrameHighlighted && renderHighlight()}

      {errorType && (
        <DataLayerBadge
          errorType={errorType}
          x={getDefaultSizeForShape(type, subtype).width / 2}
          y={getDefaultSizeForShape(type, subtype).height / 2}
        />
      )}
    </>
  );
}

export function shapeTraits(element: ShapeElement): ITraits {
  return {
    ...textEnabledTraits(element),
    shape: element.type === "shape" ? element.subtype : element.type,
    shapeStrokeColor: element.border,
    fillColor: element.fill,
    shapeStrokeWidth: parseStrokeWidth(element.strokeWidth ?? 4),
    dash: element.dash ?? 0,
  };
}

function isOldStyleShape(shape: string) {
  return shape != consts.CANVAS_ELEMENTS.SHAPE; // && Object.values(consts.SHAPES).includes(shape)
}

export function shapeValidateTrait(element: ShapeElement, trait: Trait, value: any) {
  if (trait == Trait.shape) {
    const fromOldShape = isOldStyleShape(element.type);
    const toTriangle = value == "triangle"; // triangles I want to draw with the old code, for now...
    if (fromOldShape && toTriangle) {
      const props = adjustShapeForTypeChange(element, value);
      return props;
    }

    if (fromOldShape && !toTriangle) {
      const isRect = element.type == "rect" || element.type == "rect-rounded";
      let [w, h] = isRect ? [element.width, element.height] : [element.radius * 2, element.radius * 2];
      w *= element.scaleX ?? 1;
      h *= element.scaleY ?? 1;
      const [cx, cy] = isRect ? [element.x + w / 2, element.y + h / 2] : [element.x, element.y];
      const props = {
        type: consts.CANVAS_ELEMENTS.SHAPE,
        subtype: value,
        x: cx,
        y: cy,
        width: 200,
        height: 200,
        radius: 100,
        scaleX: w / 200,
        scaleY: h / 200,
      };
      return props;
    }

    // For SVG shape transitions, to prevant deformation we scale
    // the element according to the default sizes of the new shape.
    const { width, height } = getDefaultSizeForShape(element.type, element.subtype);
    const { width: newWidth, height: newHeight } = getDefaultSizeForShape(element.type, value);
    const props = {
      type: consts.CANVAS_ELEMENTS.SHAPE,
      subtype: value,
      width: newWidth,
      height: newHeight,
      radius: 100,
      scaleX: element.scaleX * (width / newWidth),
      scaleY: element.scaleY * (height / newHeight),
      libraryPath: null, // Clears the library path when changing shape type, since it's not valid anymore
    };
    return props;
  }
  return value;
}

//When changing type from e.g. rect to circle, the position should be adjusted accordingly since konva calculates the center of these shapes differently
// This is a bad way of doing this. Konva gives us the offset property which can fix this easily
function adjustShapeForTypeChange(shape: ShapeElement, toType: string) {
  const from = shape.type;
  const fromRect = from === "rect" || from === "rect-rounded";
  const toRect = toType === "rect" || toType === "rect-rounded";
  if (fromRect && !toRect) {
    const { x, y, width, height, scaleX = 1, scaleY = 1, rotate = 0 } = shape;
    // calculate the real width and height after scaling
    const w = scaleX * width!;
    const h = scaleY * height!;
    // calculate the offset of shape's center after its rotation
    const offset = { x: w / 2, y: h / 2 };
    const rad = toRadians(rotate as Degrees);
    pointRotate(offset, rad, offset);
    const radius = Math.min(w, h) / 2;
    return {
      x: x + offset.x,
      y: y + offset.y,
      width,
      height,
      radius,
      type: toType,
      subtype: "",
      scaleX: (0.5 * w) / radius,
      scaleY: (0.5 * h) / radius,
      rotate,
    };
  } else if (!fromRect && toRect) {
    const { x, y, radius, scaleX = 1, scaleY = 1, rotate = 0 } = shape;

    const ofsX = radius! * scaleX,
      ofsY = radius! * scaleY;
    const rad = (rotate * Math.PI) / 180;
    const rotatedOfsX = ofsX * Math.cos(rad) - ofsY * Math.sin(rad);
    const rotatedOfsY = ofsX * Math.sin(rad) + ofsY * Math.cos(rad);
    return {
      x: x - rotatedOfsX,
      y: y - rotatedOfsY,
      width: radius! * 2,
      height: radius! * 2,
      radius,
      type: toType,
      subtype: "",
      scaleX,
      scaleY,
      rotate,
    };
  }
  return { ...shape, type: toType };
}
